import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useToast } from '@repo/ui/Toast/ToastRoot';
import type { WorkflowData } from '../workflow';
import { useDeliveryOptions } from './useDeliveryOptions';
import { rawString } from '../components/Language/Language';

const VALIDATION_TIMEOUT_MS = 10000;

const enum ValidationError {
  NETWORK_ERROR = 'network_error',
  ALTERNATIVE_ADDRESS_REQUIRED = 'alternative_address_required',
}

// Item payments shouldn't block method validation
// but we should figure out a more programatic way to handle this
// TODO: How do we rank api validations with the UI sequence of events

const IGNORABLE_VALIDATION_ERRORS = ['item_payment_required'] as const;

type IgnorableValidationError = (typeof IGNORABLE_VALIDATION_ERRORS)[number];

export const useMethodValidation = () => {
  const [apiValidationErrors, setApiValidationErrors] = useState<string[]>([]);
  const [hasApiValidated, setHasApiValidated] = useState(false);
  const [isApiValidating, setIsApiValidating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(false);
  const validatedRef = useRef(false);

  const translations = useMemo(
    () => ({
      validationError:
        rawString({ langKey: 'Global.delivery.validation.error' }) ||
        'The selected method is not valid. Please try another option.',
      networkError:
        rawString({ langKey: 'Global.delivery.network.error' }) ||
        'Unable to validate the selected method. Please try again.',
      selectRequired:
        rawString({ langKey: 'Global.delivery.select.required' }) ||
        'Please select a valid return method.',
      selectionError: rawString({ langKey: 'Global.delivery.selection.error' }),
    }),
    [],
  );

  const { watch, setValue, setError, clearErrors } =
    useFormContext<WorkflowData>();
  const { errorToast } = useToast();
  const { setDeliveryOption } = useDeliveryOptions({
    onSuccessSetDeliveryOption: (data) => {
      // Bail early if component unmounted
      if (!isMountedRef.current) return;

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      setIsApiValidating(false);
      setHasApiValidated(true);
      validatedRef.current = true;
      setApiValidationErrors([]);

      if (
        data?.selectDeliveryOption.validationErrors?.some(
          (error) =>
            error.code === ValidationError.ALTERNATIVE_ADDRESS_REQUIRED,
        )
      ) {
        setValue('returnMethod.isAddressRequired', true);
      }

      // Check for other validation errors, filtering out ignorable ones
      if (data?.selectDeliveryOption.validationErrors?.length) {
        const allErrors = data.selectDeliveryOption.validationErrors.map(
          (err) => err.code,
        );

        // Filter out ignorable validation errors
        const blockingErrors = allErrors.filter(
          (error) =>
            !IGNORABLE_VALIDATION_ERRORS.includes(
              error as IgnorableValidationError,
            ),
        );

        setApiValidationErrors(blockingErrors);

        // Only set form error if there are blocking errors
        if (blockingErrors.length > 0) {
          setError('returnMethod.type', {
            type: 'api_validation',
            message: translations.validationError,
          });
        } else {
          // Clear any existing API validation errors if only ignorable errors exist
          clearErrors('returnMethod.type');
        }
      } else {
        // Clear any existing API validation errors
        clearErrors('returnMethod.type');
      }
    },
    onErrorSetDeliveryOption: (_error) => {
      // Bail early if component unmounted
      if (!isMountedRef.current) return;

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Handle network errors by setting validation state to failed
      setIsApiValidating(false);
      setHasApiValidated(true);
      validatedRef.current = true;
      setApiValidationErrors([ValidationError.NETWORK_ERROR]);

      // Clear the method selection so user can retry
      setValue('returnMethod.type', undefined, {
        shouldValidate: false,
        shouldDirty: false,
      });

      // Clear any location selection as well
      setValue('returnMethod.location', undefined, {
        shouldValidate: false,
        shouldDirty: false,
      });

      // Show error toast to user
      if (translations.selectionError) {
        errorToast({ message: translations.selectionError });
      }

      // Set form error to prevent completion when API fails
      setError('returnMethod.type', {
        type: 'api_validation',
        message: translations.networkError,
      });
    },
  });

  const selectedMethod = watch('returnMethod.type');

  // Override the form's isValid state for the returnMethod task
  useEffect(() => {
    if (isApiValidating) {
      // If API is currently validating, don't set any errors
      return;
    }

    // If API has validated successfully (no errors), clear all form errors
    if (hasApiValidated && apiValidationErrors.length === 0) {
      clearErrors('returnMethod.type');
      return;
    }

    // If API has validated and found errors, set the error
    if (hasApiValidated && apiValidationErrors.length > 0) {
      const errorMessage = apiValidationErrors.includes(
        ValidationError.NETWORK_ERROR,
      )
        ? translations.networkError
        : translations.validationError;

      setError('returnMethod.type', {
        type: 'api_validation',
        message: errorMessage,
      });
      return;
    }

    // If no method is selected, set an error
    if (!selectedMethod) {
      setError('returnMethod.type', {
        type: 'custom',
        message: translations.selectRequired,
      });
      return;
    }

    // If API hasn't validated yet but method is selected, clear errors
    // (allow local validation to pass)
    clearErrors('returnMethod.type');
  }, [
    selectedMethod,
    hasApiValidated,
    apiValidationErrors,
    isApiValidating,
    setError,
    clearErrors,
    translations,
  ]);

  const setAndValidateMethod = useCallback(() => {
    if (selectedMethod) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      setIsApiValidating(true);
      validatedRef.current = false;
      setDeliveryOption();

      // Add a timeout to prevent infinite loops
      // If the API call takes longer than VALIDATION_TIMEOUT_MS, reset the validation state
      timeoutRef.current = setTimeout(() => {
        setIsApiValidating((v) => {
          if (v && !validatedRef.current) {
            setHasApiValidated(false);
            setApiValidationErrors([]);
            clearErrors('returnMethod.type');
            return false; // reset flag
          }
          return v; // no change
        });
      }, VALIDATION_TIMEOUT_MS);
    }
  }, [selectedMethod, setDeliveryOption, clearErrors]);

  const clearValidation = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsApiValidating(false);
    setApiValidationErrors([]);
    setHasApiValidated(false);
    validatedRef.current = false;
    clearErrors('returnMethod.type');
  }, [clearErrors]);

  // Cleanup timeout on unmount and set mounted flag
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isApiValidating,
    hasApiValidated,
    apiValidationErrors,
    setAndValidateMethod,
    clearValidation,
  };
};
